import { useEffect } from "react";

/**
 * Locks page scrolling behind an open drawer/sheet without moving the page.
 *
 * Does not use the `body { position: fixed }` trick: restoring the scroll offset afterwards
 * makes the page visibly jump to the top and scroll back (worse with `scroll-behavior: smooth`).
 * Instead, page scroll is disabled with overflow + a non-passive touchmove guard that only lets
 * a touch scroll an element matching `scrollerSelector` while it can still move that way.
 * Blocking those touches also stops mobile browsers from collapsing/expanding the URL bar,
 * which is what left an empty strip under full-height drawers.
 */
export default function useMobileScrollLock(active, scrollerSelector, preserveDocumentLayout = false) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      htmlScrollBehavior: html.style.scrollBehavior,
      htmlPaddingInlineEnd: html.style.paddingInlineEnd,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    if (!preserveDocumentLayout) {
      const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);
      const currentPadding = Number.parseFloat(window.getComputedStyle(html).paddingInlineEnd) || 0;

      // Keep the document width and scroll position visually stable while its scrollbar is hidden.
      if (scrollbarWidth > 0) {
        html.style.paddingInlineEnd = `${currentPadding + scrollbarWidth}px`;
      }
      html.style.overflow = "hidden";
      html.style.overscrollBehavior = "none";
      html.style.scrollBehavior = "auto";
      body.style.overflow = "hidden";
      body.style.overscrollBehavior = "none";
    }

    let startY = 0;
    const onTouchStart = (e) => {
      startY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e) => {
      if (e.touches.length > 1 || !e.cancelable) return;
      const scroller =
        scrollerSelector && e.target instanceof Element ? e.target.closest(scrollerSelector) : null;
      if (!scroller) {
        e.preventDefault();
        return;
      }
      const dy = (e.touches[0]?.clientY ?? 0) - startY;
      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      if ((dy > 0 && atTop) || (dy < 0 && atBottom)) {
        e.preventDefault();
      }
    };
    const onWheel = (e) => {
      if (!e.cancelable) return;
      const scroller =
        scrollerSelector && e.target instanceof Element ? e.target.closest(scrollerSelector) : null;
      if (!scroller) {
        e.preventDefault();
        return;
      }
      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
        e.preventDefault();
      }
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("wheel", onWheel);
      if (!preserveDocumentLayout) {
        html.style.overflow = prev.htmlOverflow;
        html.style.overscrollBehavior = prev.htmlOverscroll;
        html.style.scrollBehavior = prev.htmlScrollBehavior;
        html.style.paddingInlineEnd = prev.htmlPaddingInlineEnd;
        body.style.overflow = prev.bodyOverflow;
        body.style.overscrollBehavior = prev.bodyOverscroll;
      }
    };
  }, [active, preserveDocumentLayout, scrollerSelector]);
}
